// src/pages/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, Lock, Save, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { authAPI } from './services/api';
import Header from './components/Header';

const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // 個人資料表單
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  // 密碼表單
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // 檢查登入狀態並載入用戶資料
  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      alert(t('errors.loginRequired') || '請先登入');
      navigate('/auth');
      return;
    }

    const user = authAPI.getUser();
    setCurrentUser(user);
    setProfileData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
    });
  }, [navigate, t]);

  // 處理個人資料輸入
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    // 清除錯誤
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // 處理密碼輸入
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // 清除錯誤
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // 驗證個人資料表單
  const validateProfile = () => {
    const newErrors = {};

    if (!profileData.name.trim()) {
      newErrors.name = t('errors.required') || '此欄位為必填';
    }

    if (!profileData.email.trim()) {
      newErrors.email = t('errors.required') || '此欄位為必填';
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      newErrors.email = t('errors.invalidEmail') || '電子郵件格式不正確';
    }

    if (profileData.phone && !/^[0-9]{10,11}$/.test(profileData.phone.replace(/[-\s]/g, ''))) {
      newErrors.phone = '手機號碼格式不正確';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 驗證密碼表單
  const validatePassword = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = t('errors.required') || '請輸入當前密碼';
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = t('errors.required') || '請輸入新密碼';
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = t('errors.passwordTooShort') || '密碼至少需要 6 個字符';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = t('errors.required') || '請確認新密碼';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = t('errors.passwordMismatch') || '兩次密碼輸入不一致';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 提交個人資料更新
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;

    setLoading(true);
    setSuccessMessage('');
    setErrors({});

    try {
      // TODO: 調用後端 API 更新用戶資料
      // const response = await api.put('/user/profile', profileData);
      
      // 模擬 API 調用
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 更新本地存儲
      const updatedUser = {
        ...currentUser,
        ...profileData
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);

      // ✅ 觸發用戶狀態變更事件
      window.dispatchEvent(new Event('userStateChanged'));

      setSuccessMessage(t('success.updateSuccess') || '✅ 個人資料更新成功！');
      
      // 3秒後清除成功消息
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('更新個人資料失敗:', error);
      setErrors({ submit: error.response?.data?.message || '更新失敗，請稍後再試' });
    } finally {
      setLoading(false);
    }
  };

  // 提交密碼更新
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setLoading(true);
    setSuccessMessage('');
    setErrors({});

    try {
      // TODO: 調用後端 API 更新密碼
      // const response = await api.put('/user/password', {
      //   currentPassword: passwordData.currentPassword,
      //   newPassword: passwordData.newPassword
      // });

      // 模擬 API 調用
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccessMessage(t('success.updateSuccess') || '✅ 密碼更新成功！');
      
      // 清空密碼表單
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // 3秒後清除成功消息
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('更新密碼失敗:', error);
      setErrors({ submit: error.response?.data?.message || '更新失敗，請稍後再試' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ 頂部導航 - 使用 simple variant */}
      <Header variant="simple" title={t('user.profile') || '個人資料'} />

      {/* 麵包屑 */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-orange-500">
              {t('common.home') || '首頁'}
            </button>
            <span>/</span>
            <span className="text-gray-900 font-medium">{t('user.accountSettings') || '帳戶設定'}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* 返回按鈕 */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-500 mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t('common.back') || '返回'}</span>
          </button>

          {/* 標題 */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold text-2xl">
                {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('user.accountSettings') || '帳戶設定'}</h1>
                <p className="text-gray-600">{t('user.manageYourAccount') || '管理您的帳戶資訊'}</p>
              </div>
            </div>
          </div>

          {/* 成功消息 */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 mb-6 animate-fade-in">
              {successMessage}
            </div>
          )}

          {/* Tab 切換 */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 px-6 py-4 font-semibold transition ${
                  activeTab === 'profile'
                    ? 'bg-orange-50 text-orange-500 border-b-2 border-orange-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('user.profileInfo') || '個人資訊'}
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`flex-1 px-6 py-4 font-semibold transition ${
                  activeTab === 'password'
                    ? 'bg-orange-50 text-orange-500 border-b-2 border-orange-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('user.changePassword') || '變更密碼'}
              </button>
            </div>

            <div className="p-6">
              {/* 個人資料標籤 */}
              {activeTab === 'profile' && (
                <form onSubmit={handleProfileSubmit} className="space-y-6">
                  {/* 姓名 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.name') || '姓名'} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        name="name"
                        value={profileData.name}
                        onChange={handleProfileChange}
                        className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          errors.name
                            ? 'border-red-500 focus:ring-red-200'
                            : 'border-gray-300 focus:ring-orange-200'
                        }`}
                      />
                    </div>
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                    )}
                  </div>

                  {/* 電子郵件 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.email') || '電子郵件'} *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="email"
                        name="email"
                        value={profileData.email}
                        onChange={handleProfileChange}
                        className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          errors.email
                            ? 'border-red-500 focus:ring-red-200'
                            : 'border-gray-300 focus:ring-orange-200'
                        }`}
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* 手機號碼 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.phone') || '手機號碼'}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="tel"
                        name="phone"
                        value={profileData.phone}
                        onChange={handleProfileChange}
                        className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          errors.phone
                            ? 'border-red-500 focus:ring-red-200'
                            : 'border-gray-300 focus:ring-orange-200'
                        }`}
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  {/* 錯誤消息 */}
                  {errors.submit && (
                    <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
                      {errors.submit}
                    </div>
                  )}

                  {/* 提交按鈕 */}
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Save size={20} />
                      {loading ? t('common.saving') || '儲存中...' : t('common.save') || '儲存變更'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const user = authAPI.getUser();
                        setProfileData({
                          name: user.name || '',
                          email: user.email || '',
                          phone: user.phone || '',
                        });
                        setErrors({});
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      {t('common.cancel') || '取消'}
                    </button>
                  </div>
                </form>
              )}

              {/* 修改密碼標籤 */}
              {activeTab === 'password' && (
                <form onSubmit={handlePasswordSubmit} className="space-y-6">
                  {/* 當前密碼 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('user.currentPassword') || '當前密碼'} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className={`w-full pl-11 pr-11 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          errors.currentPassword
                            ? 'border-red-500 focus:ring-red-200'
                            : 'border-gray-300 focus:ring-orange-200'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.currentPassword && (
                      <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
                    )}
                  </div>

                  {/* 新密碼 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('user.newPassword') || '新密碼'} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        placeholder={t('errors.passwordTooShort') || '至少 6 個字符'}
                        className={`w-full pl-11 pr-11 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          errors.newPassword
                            ? 'border-red-500 focus:ring-red-200'
                            : 'border-gray-300 focus:ring-orange-200'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
                    )}
                  </div>

                  {/* 確認新密碼 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.confirmPassword') || '確認新密碼'} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                          errors.confirmPassword
                            ? 'border-red-500 focus:ring-red-200'
                            : 'border-gray-300 focus:ring-orange-200'
                        }`}
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                    )}
                  </div>

                  {/* 密碼要求提示 */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 font-medium mb-2">
                      {t('user.passwordRequirements') || '密碼要求：'}
                    </p>
                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                      <li>至少 6 個字符</li>
                      <li>建議包含大小寫字母、數字和特殊字符</li>
                    </ul>
                  </div>

                  {/* 錯誤消息 */}
                  {errors.submit && (
                    <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
                      {errors.submit}
                    </div>
                  )}

                  {/* 提交按鈕 */}
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Save size={20} />
                      {loading ? t('common.saving') || '儲存中...' : t('user.updatePassword') || '更新密碼'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: '',
                        });
                        setErrors({});
                      }}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      {t('common.cancel') || '取消'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;